import {Select} from 'antd'
import cn from 'classnames'
import React, {useContext, useEffect, useState} from 'react'
import {MdChevronLeft, MdChevronRight} from 'react-icons/md'
import useTranslate from "../../../local/useTranslate";
import {dropdownComponents} from "../../../../views/AppLocals/custom/ReactSelectComponents";
import {LayoutContext} from "../../../../components/layouts/Default/LayoutContext";

const {Option} = Select

// Tính dãy số trang hiển thị kiểu "1 2 3 ... 50", luôn giữ trang đầu, trang cuối,
// và 1 trang liền kề mỗi bên trang hiện tại; đoạn bị bỏ qua thay bằng '...'
const getPageItems = (current, total) => {
    const delta = 1
    const range = []
    for (let i = 1; i <= total; i++) {
        if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
            range.push(i)
        }
    }
    const withDots = []
    let last
    range.forEach((i) => {
        if (last !== undefined) {
            if (i - last === 2) {
                withDots.push(last + 1)
            } else if (i - last !== 1) {
                withDots.push('...')
            }
        }
        withDots.push(i)
        last = i
    })
    return withDots
}

const PageButton = ({active, disabled, onClick, children}) => (
    <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        className={cn(
            'w-8 h-8 px-2 flex flex-center rounded-lg text-sm border transition-all duration-300',
            active
                ? 'border-secondary-500 bg-secondary-500 text-white font-medium'
                : 'background border-color-100 text-color-300 hover:border-secondary-500 hover:text-secondary-600',
            disabled && 'opacity-40 cursor-not-allowed hover:border-gray-300 hover:text-color-500'
        )}>
        {children}
    </button>
)

/**
 * PaginationControls
 * Thanh điều khiển phân trang tự build theo style hệ thống hiện tại (Tailwind + antd Select),
 * không dùng antd Pagination. Gồm: tổng số item, prev/next + số trang có ellipsis,
 * chọn số item/trang, ô nhập "đi đến trang".
 */
export default function PaginationControls({
    page,
    totalPages,
    total,
    perPage,
    pageSizeOptions = [10, 20, 50, 100],
    loading,
    onChangePage,
    onChangePerPage,
}) {
    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    const [jumpValue, setJumpValue] = useState('')

    useEffect(() => {
        setJumpValue('')
    }, [page])

    const from = total === 0 ? 0 : (page - 1) * perPage + 1
    const to = Math.min(page * perPage, total)
    const pages = getPageItems(page, totalPages)

    const submitJump = () => {
        const target = parseInt(jumpValue, 10)
        if (!target || target < 1 || target > totalPages) return
        onChangePage(target)
    }

    return total > 10 && (
        <div className="w-full flex items-center justify-between flex-wrap gap-3 mt-4 pt-3 border-t border-color-100">
            {isLg && (
                <span className="text-color-300 italic">
                    {t('Showing {from} {to} of {total}', {from: from, to: to, total: total})}
                </span>
            )}


            <div className="flex items-center gap-1">
                <PageButton
                    disabled={page <= 1 || loading}
                    onClick={() => onChangePage(page - 1)}>
                    <MdChevronLeft size={16} className="m-auto" />
                </PageButton>
                {pages.map((p, i) =>
                    p === '...' ? (
                        <span key={`dots-${i}`} className="px-1 text-color-500 text-sm">
                            ...
                        </span>
                    ) : (
                        <PageButton
                            key={p}
                            active={p === page}
                            disabled={loading}
                            onClick={() => onChangePage(p)}>
                            {p}
                        </PageButton>
                    )
                )}
                <PageButton
                    disabled={page >= totalPages || loading}
                    onClick={() => onChangePage(page + 1)}>
                    <MdChevronRight size={16} className="m-auto" />
                </PageButton>
            </div>

            <div className="flex items-center gap-2">
                {isLg && (
                    <Select
                        suffixIcon={<dropdownComponents.DropdownIndicator/>}
                        value={perPage}
                        onChange={onChangePerPage}
                        getPopupContainer={(trigger) => trigger.parentNode}
                        className="rounded-lg">
                        {pageSizeOptions.map((size) => (
                            <Option key={size} value={size}>
                                {size} / {t('page') || 'trang'}
                            </Option>
                        ))}
                    </Select>
                )}
                {isLg && (
                    <span className="text-color-300 italic whitespace-nowrap">
                        {t('go to')}
                    </span>
                )}
                <input
                    type="number"
                    min={1}
                    max={totalPages}
                    value={jumpValue}
                    onChange={(e) => setJumpValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') submitJump()
                    }}
                    className="w-14 h-8 px-2 border border-color rounded-lg text-sm text-center"
                />
                <span className="text-color-300 italic whitespace-nowrap">
                    {t('page').toLowerCase()}
                </span>
            </div>
        </div>
    )
}
